import moment from 'moment';
import { useCallback, useEffect, useState } from "react";
import { toastr } from "react-redux-toastr";
import io from "socket.io-client";
import LogoSm from ".././assets/icons/logo-sm.png";
import notificationSoundMp3 from "../assets/media/sounds/notificationSound.mp3";

const useNotificationSocket = ({ getAccessTokenSilently, checkMember }) => {
	const [notifications, setNotifications] = useState([]);
	const [totalNotifications, setTotalNotifications] = useState(0);
	const [socket, setSocket] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [hasMore, setHasMore] = useState(true);
	const [skip, setSkip] = useState(1);
	const limit = 10;
	const checkMemberValidate = useCallback(() => {
		return (
			checkMember?.data?.exists ||
			(checkMember?.exists &&
				Boolean(checkMember?.user?.permissions?.permissionType) &&
				checkMember?.user?.permissions?.permissionType !== "org_admin")
		);
	}, [checkMember]);

	useEffect(() => {
		const requestNotificationPermission = async () => {
			const permission = await Notification.requestPermission();
			if (permission === "granted") {
				console.log("Notification permission granted.");
			} else if (permission === "denied") {
				console.log("Notification permission denied.");
			}
		};

		requestNotificationPermission();
	}, []);

	useEffect(() => {
		if (checkMemberValidate()) {
			return;
		}
		const initializeSocket = async () => {
			try {
				// Fetch the token asynchronously
				const token = await getAccessTokenSilently();
				const baseUrl = process.env.REACT_APP_BASE_URL
				const baseUrlWithoutVersion = process.env.REACT_APP_BASE_URL.replace(/\/v.*$/, "");
				const socketInstance = io(baseUrlWithoutVersion, {
					extraHeaders: {
						Authorization: `Bearer ${token}`,
					},
					path: "/v1/setting/socket/",
					transports: ["polling"],
				});

				setSocket(socketInstance);

				// Listen for old notifications (history)
				const handleHistory = (oldNotifications) => {
					setNotifications((prev) => [...oldNotifications, ...prev]);
					setIsLoading(false);
					if (oldNotifications.length < limit) setHasMore(false);
					socketInstance.off("history", handleHistory);
				};

				socketInstance.on("history", handleHistory);

				// Listen for new notifications
				socketInstance.on("message", (newNotification) => {
					setNotifications((prev) => [newNotification, ...prev]);
					showPushNotification(newNotification);
				});

				// Listen for paginated list responses
				socketInstance.on("list", (listNotifications) => {
					setNotifications((prev) => [...prev, ...listNotifications]);
					if (listNotifications.length < limit) setHasMore(false);
				});

				// Listen for Total Notifications
				socketInstance.on("notification", (totalNotifications) => {
					setTotalNotifications(totalNotifications);
				});
				// Handle errors
				socketInstance.on("connect_error", (err) => {
					if (socketInstance) {
						socketInstance.disconnect();
						setIsLoading(false);
						setHasMore(false);
					}
					// toastr.error("Error", err.message ?? "Something went wrong");
					console.error("WebSocket connection error:", err.message);
				});
				setIsLoading(false);
			} catch (error) {
				toastr.error("Error", error?.data?.message ?? "Something went wrong");
				console.error("Error fetching token or connecting to WebSocket:", error);
			}
		};

		// Call the function to initialize the socket connection
		initializeSocket();

		return () => {
			if (socket) {
				socket.disconnect();
			}
		};
	}, []);

	useEffect(() => {
		if (socket && skip > 1) {
			socket.emit("list", { skip, limit });
		}
	}, [skip, limit, socket]);

	const fetchMoreNotifications = () => {
		setSkip((prevSkip) => prevSkip + 1);
	};

	const markAsRead = useCallback(
		(notification) => {
			if (socket) {
				// Emit the read event
				socket.emit("read", {
					id: notification?._id,
					read: !notification?.read,
				});
				setNotifications((prev) =>
					prev.map((n) => (n._id === notification._id ? { ...n, read: !notification?.read } : n))
				);
			}
		},
		[socket]
	);

	const markAllAsRead = useCallback(() => {
		if (socket) {
			socket.emit("allRead");
			setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
			setTotalNotifications(0);
		}
	}, [socket]);

	const stripHTML = (html) => {
		const tempDiv = document.createElement("div");
		tempDiv.innerHTML = html;
		return tempDiv.textContent || tempDiv.innerText || "";
	};

	const showPushNotification = (notification) => {
		const notificationSound = localStorage.getItem("notificationSound") ? JSON.parse(localStorage.getItem("notificationSound")) : true;
		const message = stripHTML(notification.message);
		const addedBy = notification?.type === "manual" ? notification?.createdBy?.name : "System";
		const createdDate = `${moment(notification?.createdAt).fromNow()}`;
		const profileImg =
			notification?.type === "manual" ? notification?.createdBy?.contactId?.profile_img || LogoSm : LogoSm;
		if (Notification.permission === "granted") {
			new Notification(message, {
				body: `Added by ${addedBy} ${createdDate}`,
				icon: profileImg,
				silent: !notificationSound
			});
			if(!notificationSound) return;
			new Audio(notificationSoundMp3).play();
		}
	};

	return {
		notifications,
		totalNotifications,
		markAsRead,
		markAllAsRead,
		fetchMoreNotifications,
		hasMore,
		isLoading,
	};
};

export default useNotificationSocket;
